import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useFunzioniSbloccateStore } from '@/stores/funzioniSbloccate'
import { haRuoloAmministratore } from '@/utils/ruoli'

/**
 * Estendi RouteMeta per TypeScript strict.
 * - requiresAuth: true  → guard reindirizza a login se non autenticato
 * - publicLayout: true  → App.vue NON usa AppLayout (es. pagina login)
 */
declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    publicLayout?: boolean
    helpSlug?: string
    hasTour?: boolean
    breadcrumb?: { label: string; to?: string }[]
    /** Permesso richiesto per accedere alla route (oltre a requiresAuth). */
    permission?: string
    /** In alternativa a `permission`: basta uno di questi permessi (come `@perm.has(a) or @perm.has(b)` nel backend). */
    permissionAny?: string[]
    /** Servono TUTTI questi permessi (es. scheda nuova: scrittura e lettura, perché le chiamate di supporto vogliono la lettura). */
    permissionAll?: string[]
    /**
     * Consente l'accesso anche all'AMMINISTRATORE DELL'AZIENDA (super-admin o flag
     * `utenti.amministratore`) pur senza il permesso indicato in `permission`. Da usare solo
     * per pagine i cui dati il backend confina alla propria azienda.
     */
    oppureAmministratoreAzienda?: boolean
    /**
     * Con `permissionAny`: apre la route anche a chi ha il RUOLO amministratore (come `s:hasRole('amministratore')` del
     * classico) pur senza nessuno dei permessi elencati. Solo UX: il server decide (403).
     */
    oppureRuoloAmministratore?: boolean
    /** La route si apre solo in sessione passpartout (claim del token): serve alla gestione delle funzioni sbloccate. */
    soloPasspartout?: boolean
    /**
     * Chiave del catalogo passpartout (`FunzionePasspartout`): la route si apre se la funzione è visibile
     * (sessione passpartout OPPURE sbloccata per l'azienda/tutte), vedi `useFunzioniSbloccate().visibile`.
     * Solo UX: il backend ricontrolla con `@ppGate.consente`.
     */
    passpartoutKey?: string
    title?: string
    /** ID pagina per usePersonalizzazione — abilita TOP_BAR e BOTTOM_BAR nel layout. */
    idPagina?: string
    /** Sezione mostrata da una vista condivisa (es. CentroSezioneView: convenzioni | cassa | articoli). */
    sezione?: string
  }
}

const routes: RouteRecordRaw[] = [
  // Redirect radice
  {
    path: '/',
    redirect: '/dashboard',
  },

  // --- Route pubbliche (no shell Sakai) --------------------------------
  // NOTA: NON usare /auth/* per route Vue — Vite proxy manda /auth/* al backend.
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { requiresAuth: false, publicLayout: true },
  },

  // Sito vetrina pubblico (immigreer.it): nessun dato, nessuna chiamata API.
  // Vive nell'app per riusare porte/domini già cablati (dev :5176+, prod
  // accesso./gestionale.immigreer.it) invece di richiedere un vhost dedicato.
  {
    path: '/landingpage',
    name: 'landingpage',
    component: () => import('@/views/LandingPageView.vue'),
    meta: { requiresAuth: false, publicLayout: true },
  },

  // Pagina di stato PUBBLICA del servizio: mostra se il portale (backend Spring + DB) è operativo.
  // Nessun dato sensibile: polla solo GET /api/v2/actuator/health (permitAll, show-details anonimo
  // = solo status). Serve agli utenti quando il nuovo portale non è raggiungibile.
  {
    path: '/status',
    name: 'status',
    component: () => import('@/views/StatusView.vue'),
    meta: { requiresAuth: false, publicLayout: true, title: 'Stato del servizio' },
  },

  // Atterraggio del link "Ripristina password" ricevuto via email (token monouso 72h).
  {
    path: '/reset-password/:token',
    name: 'reset-password',
    component: () => import('@/views/ResetPasswordView.vue'),
    meta: { requiresAuth: false, publicLayout: true },
  },

  // Voce di menu "Portale Classico": reindirizza al vecchio portale Seam.
  // E' una route Vue (cosi' la voce di menu e' is_legacy=0 e visibile a tutti),
  // ma il suo unico scopo e' fare il redirect esterno al legacy.
  {
    path: '/portale-classico',
    name: 'portale-classico',
    component: () => import('@/views/DashboardView.vue'), // placeholder mai renderizzato
    meta: { requiresAuth: true },
    beforeEnter: () => {
      const legacyBase = import.meta.env.VITE_LEGACY_BASE_URL ?? '/legacy'
      // SENZA ?pagina=: il parametro vuole il percorso di un facelet (come fa useLegacyLink per
      // le pagine profonde), e qui c'era 'dashboardHome', che è il nome di un PERMESSO — lo si
      // vede in home.xhtml come navigator.hasPermission('dashboardHome'). Il legacy non trovava
      // il facelet, scriveva un avviso nel log e ripiegava sulla home: funzionava per il
      // paracadute, non perché il link fosse giusto.
      //
      // Senza parametro ci pensa il template: home.xhtml include
      // `#{page != null ? page : '/pages/home.xhtml'}`. Passare il percorso vero della home
      // sarebbe peggio — lo renderebbe un deep-link, sottoposto a isPaginaPermessa, e chi non
      // ha un permesso su quella pagina si vedrebbe negare la propria home.
      window.location.href = `${legacyBase}/home.seam`
      return false // annulla la navigazione interna: il browser sta gia' uscendo
    },
  },

  // Vista affiancata: due pagine in due pannelli con navigazione indipendente.
  {
    path: '/workspace',
    name: 'workspace',
    component: () => import('@/modules/workspace/views/SplitWorkspace.vue'),
    meta: { requiresAuth: true, helpSlug: 'workspace', title: 'Vista affiancata' },
  },

  // --- Route protette (con shell Sakai tramite AppLayout) --------------
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('@/views/DashboardView.vue'),
    meta: { requiresAuth: true, helpSlug: 'dashboard', hasTour: true },
  },
  {
    path: '/istituzione/panoramica',
    name: 'istituzione-panoramica',
    component: () => import('@/modules/dashboard/views/PanoramicaIstituzioneView.vue'),
    meta: {
      hasTour: true,
      requiresAuth: true,
      permission: 'BENEFICIARI_READ',
      helpSlug: 'istituzione-panoramica',
      idPagina: 'ISTITUZIONE_PANORAMICA',
    },
  },
  {
    path: '/calendario/tipologie',
    name: 'calendario-tipologie',
    component: () =>
      import('@/modules/calendario/views/TipologieAppuntamentiList.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'CALENDARIO_TIPOLOGIE_READ', helpSlug: 'calendario-tipologie' },
  },
  {
    path: '/calendario/tipologie/medici',
    name: 'calendario-tipologie-medici',
    component: () =>
      import('@/modules/calendario/views/TipologieAppuntamentiMediciView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'CALENDARIO_TIPOLOGIE_READ', helpSlug: 'calendario-tipologie-medici' },
  },
  {
    path: '/calendario/tipologie/sociali',
    name: 'calendario-tipologie-sociali',
    component: () =>
      import('@/modules/calendario/views/TipologieAppuntamentiSocialiView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'CALENDARIO_TIPOLOGIE_READ', helpSlug: 'calendario-tipologie-sociali' },
  },
  {
    path: '/calendario/calendario',
    name: 'calendario-google',
    component: () => import('@/modules/calendario/views/CalendarioGoogleView.vue'),
    meta: { requiresAuth: true, permission: 'CALENDARIO_APPUNTAMENTI_READ', helpSlug: 'calendario', hasTour: true, idPagina: 'CALENDARIO' },
  },
  // NOTA: CalendarioVueView.vue (versione vue-cal) e CalendarioWidgetVue.vue sono mantenuti
  // come file di riferimento nel repository ma NON registrati come route — vue-cal è stato
  // rimosso dal progetto e non può essere bundlato.
  // --- Personale -----------------------------------------------------------
  {
    path: '/personale',
    name: 'personale-list',
    component: () => import('@/modules/personale/views/PersonaleListView.vue'),
    meta: { requiresAuth: true, helpSlug: 'personale-list', hasTour: true, idPagina: 'PERSONALE_LIST' },
  },
  {
    path: '/personale/:id/edit',
    name: 'personale-edit',
    component: () => import('@/modules/personale/views/PersonaleEditView.vue'),
    meta: { requiresAuth: true, permission: 'PERSONALE_WRITE', helpSlug: 'personale-edit' },
  },
  {
    path: '/personale/mansioni',
    name: 'personale-mansioni',
    component: () => import('@/modules/personale/views/MansioniView.vue'),
    meta: {
      hasTour: true,
      requiresAuth: true,
      permission: 'MANSIONI_READ',
      helpSlug: 'mansioni',
      idPagina: 'MANSIONI',
    },
  },
  {
    path: '/personale/presenti',
    name: 'personale-presenti-list',
    component: () => import('@/modules/personale/views/PersonalePresenteListView.vue'),
    meta: { hasTour: true, requiresAuth: true, helpSlug: 'personale-presenti-list', permission: 'PERSONALE_PRESENTE_READ', idPagina: 'PERSONALE_PRESENTI' },
  },
  {
    // Combacia con la spring_route della funzione legacy 6000 «Log attività» (V875)
    path: '/personale/log-attivita',
    name: 'personale-log-attivita',
    component: () => import('@/modules/logattivita/views/LogAttivitaView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'LOG_ATTIVITA_READ',
      title: 'Log attività',
      idPagina: 'LOG_ATTIVITA',
      helpSlug: 'log-attivita',
      breadcrumb: [
        { label: 'Gestione Personale' },
        { label: 'Log attività', to: '/personale/log-attivita' },
      ],
    },
  },

  // --- Ospiti (beneficiari) ------------------------------------------------
  {
    path: '/ospiti',
    name: 'ospiti-list',
    component: () => import('@/modules/ospiti/views/OspitiListView.vue'),
    meta: {
      hasTour: true,
      requiresAuth: true,
      permission: 'BENEFICIARI_READ',
      helpSlug: 'ospiti-list',
      idPagina: 'OSPITI',
    },
  },
  {
    path: '/ospiti/:id/edit',
    name: 'ospite-edit',
    component: () => import('@/modules/ospiti/views/OspiteEditView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'BENEFICIARI_WRITE',
      helpSlug: 'ospiti-modifica-anagrafica',
      idPagina: 'OSPITI_EDIT',
      hasTour: true,
    },
  },
  // Elenco ospiti vulnerabili (funz. 719): permesso unico anche per stampa/export
  {
    path: '/ospiti-vulnerabili',
    name: 'ospiti-vulnerabili',
    component: () => import('@/modules/vulnerabilita/views/OspitiVulnerabiliView.vue'),
    meta: {
      hasTour: true,
      requiresAuth: true,
      permission: 'OSPITI_VULNERABILI_READ',
      helpSlug: 'ospiti-vulnerabili',
      idPagina: 'OSPITI_VULNERABILI',
    },
  },

  // --- Timbrature ----------------------------------------------------------
  {
    path: '/timbrature/segnalazioni',
    name: 'timbrature-segnalazioni',
    component: () => import('@/modules/timbrature/views/AnomalieTimbratureView.vue'),
    meta: { hasTour: true, requiresAuth: true, helpSlug: 'timbrature-segnalazioni', permission: 'TIMBRATURE_READ' },
  },

  // --- Admin: gestione guide contestuali ----------------------------------
  {
    path: '/admin/guide',
    name: 'admin-guide',
    component: () => import('@/modules/guida/views/GuidaAdminView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'PLATFORM_SUPERADMIN', helpSlug: 'admin-guide' },
  },

  // --- Admin: gestione ruoli e permessi -----------------------------------
  {
    path: '/admin/roles',
    name: 'admin-roles',
    component: () => import('@/modules/admin/views/RolesListView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'PLATFORM_SUPERADMIN', helpSlug: 'admin-ruoli' },
  },
  {
    path: '/admin/roles/:id',
    name: 'admin-role-detail',
    component: () => import('@/modules/admin/views/RoleDetailView.vue'),
    meta: { requiresAuth: true, permission: 'PLATFORM_SUPERADMIN', helpSlug: 'admin-role-detail' },
  },
  {
    // Niente `permission`: l'accesso lo decide il gate passpartout (`passpartoutKey`: passpartout oppure funzione
    // sbloccata per l'azienda). È solo UX: il backend è autoritativo (`@ppGate.consente`).
    path: '/admin/user-permissions',
    name: 'admin-user-permissions',
    component: () => import('@/modules/admin/views/UserPermissionsView.vue'),
    meta: { hasTour: true, requiresAuth: true, passpartoutKey: 'admin.permessiUtente', helpSlug: 'admin-user-permissions' },
  },
  {
    path: '/admin/traduzioni',
    name: 'admin-traduzioni',
    component: () => import('@/modules/admin/views/AdminTraduzioniView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'PLATFORM_SUPERADMIN', helpSlug: 'admin-traduzioni', idPagina: 'ADMIN_TRADUZIONI' },
  },
  {
    path: '/admin/diagnostics',
    name: 'admin-diagnostics',
    component: () => import('@/modules/admin/views/PermissionDiagnosticsView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'PLATFORM_SUPERADMIN', helpSlug: 'admin-diagnostics' },
  },
  {
    path: '/admin/sse-debug',
    name: 'admin-sse-debug',
    component: () => import('@/modules/admin/views/SseDebugView.vue'),
    meta: { requiresAuth: true, permission: 'PLATFORM_SUPERADMIN', title: 'Comunicazioni di sistema', helpSlug: 'admin-sse-debug' },
  },
  {
    // Funzione 9145 (V1437), solo super-admin
    path: '/admin/sessioni-automatiche',
    name: 'admin-sessioni-automatiche',
    component: () => import('@/modules/admin/views/SessioniAutomaticheView.vue'),
    meta: { requiresAuth: true, permission: 'PLATFORM_SUPERADMIN', title: 'Sessioni automatiche', helpSlug: 'sessioni-automatiche' },
  },
  {
    path: '/admin/notifiche-log',
    name: 'admin-notifiche-log',
    component: () => import('@/modules/admin/views/NotificheLogView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'PLATFORM_SUPERADMIN', helpSlug: 'admin-notifiche-log' },
  },
  {
    // Combacia con la spring_route della funzione legacy 210 "Anagrafica Farmaci" (V447)
    path: '/area-medica/farmaci',
    name: 'area-medica-farmaci',
    component: () => import('@/modules/farmaci/views/FarmaciView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'FARMACI_READ',
      title: 'Anagrafica farmaci',
      // Lo stesso id della riga globale di configurazione_pagina seminata in V447
      idPagina: 'FARMACI',
      helpSlug: 'farmaci',
      breadcrumb: [
        { label: 'Area Medica' },
        { label: 'Anagrafica farmaci', to: '/area-medica/farmaci' },
      ],
    },
  },
  {
    // Gestione del catalogo dell'hub (aree e voci): solo super-admin di piattaforma, fuori dal menu.
    // Segmento statico: ha la precedenza su `/configurazione/:area?` (il codice `catalogo` è riservato).
    path: '/configurazione/catalogo',
    name: 'configurazione-catalogo',
    component: () => import('@/modules/configurazione/views/ConfigurazioneCatalogoView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'PLATFORM_SUPERADMIN',
      hasTour: true,
      title: 'Catalogo della Configurazione',
      helpSlug: 'configurazione-catalogo',
      breadcrumb: [
        { label: 'Configurazione', to: '/configurazione' },
        { label: 'Catalogo', to: '/configurazione/catalogo' },
      ],
    },
  },
  {
    // Hub «Configurazione Admin»: stesso componente dell'hub Configurazione, endpoint gemello. Fuori dal menu
    // statico (la foglia la porta il menu del backend, nodo 2000). Nessun permesso lato client: il backend è
    // autoritativo (403 → schermata di permesso mancante). `/configurazione-admin/:area` porta a quell'area.
    path: '/configurazione-admin/:area?',
    name: 'configurazione-admin',
    // Componente distinto (wrapper): garantisce il rimontaggio navigando tra i due hub.
    component: () => import('@/modules/configurazione/views/ConfigurazioneAdminHubView.vue'),
    meta: {
      requiresAuth: true,
      hasTour: true,
      title: 'Configurazione Admin',
      helpSlug: 'configurazione-admin',
      breadcrumb: [{ label: 'Configurazione Admin', to: '/configurazione-admin' }],
    },
  },
  {
    // Indice delle configurazioni per area. Nessun permesso dedicato: il backend restituisce
    // solo le voci a cui il profilo ha accesso. `/configurazione/:area` porta a quell'area.
    path: '/configurazione/:area?',
    name: 'configurazione',
    component: () => import('@/modules/configurazione/views/ConfigurazioneHubView.vue'),
    meta: {
      requiresAuth: true,
      hasTour: true,
      title: 'Configurazione',
      idPagina: 'CONFIGURAZIONE_HUB',
      helpSlug: 'configurazione-hub',
      breadcrumb: [{ label: 'Configurazione', to: '/configurazione' }],
    },
  },
  {
    // Combacia con la spring_route della funzione legacy 23 "Elenco Lettori" (V445)
    path: '/admin/lettori-badge',
    name: 'admin-lettori-badge',
    component: () => import('@/modules/lettori/views/LettoriView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'LETTORI_BADGE_READ',
      title: 'Elenco Lettori',
      // Lo stesso id della riga globale di configurazione_pagina seminata in V445
      idPagina: 'LETTORI_BADGE',
      helpSlug: 'lettori-badge',
      breadcrumb: [
        { label: 'Amministrazione' },
        { label: 'Elenco Lettori', to: '/admin/lettori-badge' },
      ],
    },
  },
  {
    // Associazioni badge (V733): si apre dall'Elenco Lettori, con lo stesso permesso
    path: '/admin/lettori-badge/associazioni',
    name: 'admin-lettori-associazioni-badge',
    component: () => import('@/modules/lettori/views/AssociazioniBadgeView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'LETTORI_BADGE_READ',
      title: 'Associazioni badge',
      helpSlug: 'lettori-associazioni-badge',
      breadcrumb: [
        { label: 'Amministrazione' },
        { label: 'Elenco Lettori', to: '/admin/lettori-badge' },
        { label: 'Associazioni badge', to: '/admin/lettori-badge/associazioni' },
      ],
    },
  },
  {
    // Combacia con la spring_route della funzione legacy 19 "Log Accessi" (V444)
    path: '/admin/log-accessi',
    name: 'admin-log-accessi',
    component: () => import('@/modules/logaccessi/views/LogAccessiView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'LOG_ACCESSI_READ',
      title: 'Log Accessi',
      // Lo stesso id della riga globale di configurazione_pagina seminata in V444
      idPagina: 'LOG_ACCESSI',
      helpSlug: 'log-accessi',
      breadcrumb: [
        { label: 'Amministrazione' },
        { label: 'Log Accessi', to: '/admin/log-accessi' },
      ],
    },
  },
  {
    // Combacia con la spring_route della funzione legacy 16 "Log Utenti" (V443)
    path: '/admin/log-utenti',
    name: 'admin-log-utenti',
    component: () => import('@/modules/logutenti/views/LogUtentiView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'LOG_UTENTI_READ',
      title: 'Log Utenti',
      idPagina: 'LOG_UTENTI',
      helpSlug: 'log-utenti',
      breadcrumb: [
        { label: 'Amministrazione' },
        { label: 'Log Utenti', to: '/admin/log-utenti' },
      ],
    },
  },
  {
    path: '/admin/menu',
    name: 'admin-menu',
    component: () => import('@/modules/admin/views/MenuAdminView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'PLATFORM_SUPERADMIN', helpSlug: 'admin-menu' },
  },
  {
    path: '/admin/ai-config',
    name: 'admin-ai-config',
    component: () => import('@/modules/admin/views/AdminAiConfigView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'PLATFORM_SUPERADMIN', title: 'Configurazione AI', helpSlug: 'admin-ai-config' },
  },
  {
    path: '/admin/errori-log',
    name: 'admin-errori-log',
    component: () => import('@/modules/admin/views/AdminErroriLogView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'TICKET_OPERATOR', title: 'Errori applicativi', helpSlug: 'admin-errori-log' },
  },
  {
    path: '/admin/impostazioni',
    name: 'admin-impostazioni',
    component: () => import('@/modules/admin/views/AdminImpostazioniView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'PLATFORM_SUPERADMIN', title: 'Impostazioni piattaforma', helpSlug: 'admin-impostazioni' },
  },
  {
    path: '/admin/aziende',
    name: 'admin-aziende',
    component: () => import('@/modules/admin/views/AziendeElencoView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'PLATFORM_SUPERADMIN', title: 'Aziende', helpSlug: 'admin-aziende' },
  },
  {
    // La pagina «Azienda» aperta dal super-admin su un'azienda dell'elenco (AziendaEdit del classico da AziendaList).
    path: '/admin/aziende/:id(\\d+)/modifica',
    name: 'admin-azienda-modifica',
    component: () => import('@/modules/azienda/views/AziendaEditView.vue'),
    props: (route) => ({ aziendaId: Number(route.params.id) }),
    meta: { requiresAuth: true, permission: 'PLATFORM_SUPERADMIN', title: 'Modifica azienda', helpSlug: 'azienda-edit', idPagina: 'ADMIN_AZIENDA_MODIFICA' },
  },
  {
    path: '/admin/nuova-azienda',
    name: 'admin-nuova-azienda',
    component: () => import('@/modules/admin/views/NuovaAziendaView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'PLATFORM_SUPERADMIN', title: 'Attiva nuova azienda', helpSlug: 'admin-nuova-azienda' },
  },
  {
    path: '/admin/configurazione-iniziale',
    name: 'admin-configurazione-iniziale',
    component: () => import('@/modules/admin/views/ConfigurazioneInizialeView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'PLATFORM_SUPERADMIN', title: 'Configurazione iniziale azienda', helpSlug: 'admin-configurazione-iniziale' },
  },
  {
    path: '/admin/openapi-servizi',
    name: 'admin-openapi-servizi',
    component: () => import('@/modules/admin/views/AdminCatalogoOpenApiView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'PLATFORM_SUPERADMIN', title: 'Catalogo servizi OpenAPI', helpSlug: 'admin-openapi-servizi' },
  },
  {
    path: '/admin/novita',
    name: 'admin-novita',
    component: () => import('@/modules/admin/views/AdminNovitaView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'PLATFORM_SUPERADMIN', title: 'Novità', helpSlug: 'admin-novita' },
  },
  {
    path: '/admin/spazio-disco',
    name: 'admin-spazio-disco',
    component: () => import('@/modules/admin/views/AdminSpazioDiscoView.vue'),
    // Come Consumo AI: in menu solo per il super-admin (sotto Configurazione Admin), ma la pagina è aperta
    // anche all'amministratore dell'azienda, che vede solo la propria (lo confina il backend).
    meta: { hasTour: true, requiresAuth: true, permission: 'PLATFORM_SUPERADMIN', oppureAmministratoreAzienda: true, title: 'Spazio occupato', helpSlug: 'admin-spazio-disco', idPagina: 'ADMIN_SPAZIO_DISCO' },
  },
  {
    path: '/admin/ai-consumo',
    name: 'admin-ai-consumo',
    component: () => import('@/modules/admin/views/AdminAiConsumoView.vue'),
    // Oltre agli amministratori di piattaforma, la pagina è aperta agli AMMINISTRATORI
    // DELL'AZIENDA: è la loro azienda a pagare i token, e ci arrivano dal widget in dashboard
    // (in menu la voce resta sotto Configurazione Admin, quindi solo di piattaforma).
    // Il backend confina comunque i dati alla propria azienda.
    meta: { hasTour: true, requiresAuth: true, permission: 'ADMIN_AI_CONFIG', oppureAmministratoreAzienda: true, title: 'Consumo AI', helpSlug: 'admin-ai-consumo', idPagina: 'ADMIN_AI_CONSUMO' },
  },
  {
    path: '/admin/session-config',
    name: 'admin-session-config',
    component: () => import('@/modules/admin/views/AdminSessionConfigView.vue'),
    meta: {
      hasTour: true,
      requiresAuth: true,
      permission: 'ADMIN_SESSION_CONFIG',
      title: 'Configurazione Sessione',
      helpSlug: 'admin-session-config',
      idPagina: 'ADMIN_SESSION_CONFIG',
    },
  },
  {
    path: '/admin/schedulers',
    name: 'admin-schedulers',
    component: () => import('@/modules/admin/views/SchedulerAdminView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'PLATFORM_SUPERADMIN', title: 'Gestione Scheduler', helpSlug: 'admin-schedulers' },
  },
  {
    path: '/admin/chatbot-feedback',
    name: 'admin-chatbot-feedback',
    component: () => import('@/modules/admin/views/ChatbotFeedbackView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'PLATFORM_SUPERADMIN', title: 'Feedback Chatbot AI', helpSlug: 'chatbot-feedback-stats' },
  },
  {
    path: '/admin/permission-log',
    name: 'admin-permission-log',
    component: () => import('@/modules/admin/views/PermissionLogView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'AUTH_ROLE_ADMIN', title: 'Log Modifiche Permessi', helpSlug: 'admin-permission-log' },
  },

  // --- Messaggi broadcast ---
  {
    path: '/messaggi',
    name: 'messaggi-list',
    component: () => import('@/modules/messaggi/views/MessaggiListView.vue'),
    meta: { requiresAuth: true, helpSlug: 'messaggi-list', idPagina: 'MESSAGGI_LIST', hasTour: true },
  },

  // --- Richieste istituzionali ---------------------------------------------
  {
    path: '/richieste',
    name: 'richieste',
    component: () => import('@/modules/richieste/views/RichiesteListView.vue'),
    meta: {
      hasTour: true,
      requiresAuth: true,
      permission: 'RICHIESTE_ISTITUZIONALI_READ',
      helpSlug: 'richieste-istituzionali',
      idPagina: 'RICHIESTE_ISTITUZIONALI',
    },
  },
  {
    path: '/richieste/:id',
    name: 'richiesta-detail',
    component: () => import('@/modules/richieste/views/RichiestaDetailView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'RICHIESTE_ISTITUZIONALI_READ',
      helpSlug: 'richieste-istituzionali',
      idPagina: 'RICHIESTE_ISTITUZIONALI',
    },
  },

  // --- Richieste ferie/permessi (funzioni 2103 «Le mie richieste» e 1304 «Richieste del personale») ---
  {
    path: '/permessi-personali',
    name: 'permessi-personali',
    component: () => import('@/modules/permessipersonale/views/LeMieRichiesteView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'PERMESSO_PERSONALE_READ',
      hasTour: true,
      helpSlug: 'permessi-personali',
      idPagina: 'PERMESSI_PERSONALI',
      title: 'Le mie richieste',
    },
  },
  {
    path: '/permessi-personali/lavorazione',
    name: 'permessi-personali-lavorazione',
    component: () => import('@/modules/permessipersonale/views/RichiestePersonaleView.vue'),
    meta: {
      requiresAuth: true,
      // come @PreAuthorize di GET /lavorazione: READ e almeno una fra VERIFICA, AUTORIZZA, ELIMINA
      permission: 'PERMESSO_PERSONALE_READ',
      permissionAny: ['PERMESSO_PERSONALE_VERIFICA', 'PERMESSO_PERSONALE_AUTORIZZA', 'PERMESSO_PERSONALE_ELIMINA'],
      // …oppure il ruolo amministratore, come nel classico (permessiTable.xhtml): rifiuta ed elimina senza 1305/1306/1309
      oppureRuoloAmministratore: true,
      hasTour: true,
      helpSlug: 'permessi-personali-lavorazione',
      idPagina: 'PERMESSI_PERSONALI',
      title: 'Richieste del personale',
      breadcrumb: [
        { label: 'Le mie richieste', to: '/permessi-personali' },
        { label: 'Richieste del personale', to: '/permessi-personali/lavorazione' },
      ],
    },
  },

  // Collegamento a una richiesta (link di campanella e push: /permessi-personali/{id}). Non ha una pagina sua: decide quale
  // elenco aprire (lavorazione o «Le mie richieste») e vi apre il dettaglio (?apri=). La visibilità resta del server.
  {
    path: '/permessi-personali/:id(\\d+)',
    name: 'permessi-personali-richiesta',
    component: () => import('@/modules/permessipersonale/views/RichiestaCollegamentoView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'PERMESSO_PERSONALE_READ',
      title: 'Richiesta',
    },
  },

  // --- Ticketing / Helpdesk ---
  {
    path: '/ticket',
    name: 'ticket-list',
    component: () => import('@/modules/ticketing/views/TicketListView.vue'),
    meta: { hasTour: true, requiresAuth: true, helpSlug: 'ticket-lista' },
  },
  {
    path: '/ticket/nuovo',
    name: 'ticket-new',
    component: () => import('@/modules/ticketing/views/TicketNewView.vue'),
    meta: { hasTour: true, requiresAuth: true, helpSlug: 'ticket-nuovo' },
  },
  {
    path: '/ticket/:id',
    name: 'ticket-detail',
    component: () => import('@/modules/ticketing/views/TicketDetailView.vue'),
    meta: { requiresAuth: true, helpSlug: 'ticket-dettaglio' },
  },
  {
    // Aperta a ogni utente autenticato: nessun permesso, come la voce di menu (V1090)
    path: '/assistenza/come-ti-aiutiamo',
    name: 'assistenza-come-ti-aiutiamo',
    component: () => import('@/modules/ticketing/views/ComeTiAiutiamoView.vue'),
    meta: { requiresAuth: true, helpSlug: 'assistenza-come-ti-aiutiamo' },
  },
  {
    path: '/helpdesk',
    name: 'helpdesk',
    component: () => import('@/modules/ticketing/views/TicketListView.vue'),
    meta: { hasTour: true, requiresAuth: true, helpSlug: 'helpdesk-coda' },
  },
  {
    path: '/helpdesk/config',
    name: 'helpdesk-config',
    component: () => import('@/modules/ticketing/views/TicketAdminView.vue'),
    meta: { hasTour: true, requiresAuth: true, helpSlug: 'helpdesk-config' },
  },

  // --- Diario del Centro --------------------------------------------------
  {
    path: '/diario-centro',
    name: 'diario-centro',
    component: () => import('@/modules/diario/views/DiarioCentroView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'DIARIO_READ',
      helpSlug: 'diario-centro',
      hasTour: true,
      idPagina: 'DIARIO_CENTRO',
    },
  },
  {
    path: '/diario-centro/etichette',
    name: 'diario-centro-etichette',
    component: () => import('@/modules/diario/views/EtichetteDiarioView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'DIARIO_ETICHETTE_READ',
      helpSlug: 'diario-centro-etichette',
      idPagina: 'DIARIO_ETICHETTE',
      title: 'Etichette Diario',
    },
  },

  // --- Registro visitatori (funzione legacy 219) ---------------------------
  {
    path: '/registro-visitatori',
    name: 'registro-visitatori',
    component: () => import('@/modules/visitatori/views/RegistroVisitatoriView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'REGISTRO_VISITATORI_READ', helpSlug: 'registro-visitatori', idPagina: 'REGISTRO_VISITATORI', title: 'Registro visitatori', breadcrumb: [{ label: 'Gestione Centri' }, { label: 'Registro visitatori', to: '/registro-visitatori' }] },
  },
  {
    path: '/registro-visitatori/nuovo',
    name: 'registro-visitatori-nuovo',
    component: () => import('@/modules/visitatori/views/RegistroVisitatoreEditView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'REGISTRO_VISITATORI_WRITE', helpSlug: 'registro-visitatori', idPagina: 'REGISTRO_VISITATORI', title: 'Registra visitatore', breadcrumb: [{ label: 'Gestione Centri' }, { label: 'Registro visitatori', to: '/registro-visitatori' }, { label: 'Registra visitatore' }] },
  },
  {
    path: '/registro-visitatori/:id(\\d+)',
    name: 'registro-visitatori-edit',
    component: () => import('@/modules/visitatori/views/RegistroVisitatoreEditView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'REGISTRO_VISITATORI_READ', helpSlug: 'registro-visitatori', idPagina: 'REGISTRO_VISITATORI', title: 'Visitatore', breadcrumb: [{ label: 'Gestione Centri' }, { label: 'Registro visitatori', to: '/registro-visitatori' }, { label: 'Visitatore' }] },
  },

  // --- Contabilità --------------------------------------------------------
  {
    path: '/contabilita/rendiconto',
    name: 'contabilita-rendiconto',
    component: () => import('@/modules/contabilita/views/RendicontoView.vue'),
    meta: { hasTour: true,
      requiresAuth: true,
      permission: 'CONTABILITA_RENDICONTO_READ',
      helpSlug: 'contabilita-rendiconto',
      idPagina: 'CONTABILITA_RENDICONTO',
    },
  },
  {
    path: '/contabilita/voci-spesa',
    name: 'contabilita-voci-spesa',
    component: () => import('@/modules/contabilita/views/VociSpesaView.vue'),
    meta: { hasTour: true, requiresAuth: true, permissionAny: ['CONTABILITA_VOCI_SPESA_READ', 'CONTABILITA_VOCI_SPESA_WRITE'], helpSlug: 'contabilita-voci-spesa', idPagina: 'CONTABILITA_VOCI_SPESA', title: 'Voci di spesa' },
  },
  {
    path: '/contabilita/import-massivo',
    name: 'contabilita-import-massivo',
    component: () => import('@/modules/contabilita/views/ImportMassivoView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'CONTABILITA_DOCUMENTI_READ', helpSlug: 'contabilita-import-massivo', idPagina: 'IMPORT_MASSIVO', title: 'Import massivo' },
  },
  {
    path: '/contabilita/scadenze-pagamenti',
    name: 'contabilita-scadenze-pagamenti',
    component: () => import('@/modules/contabilita/views/ScadenzePagamentiView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'CONTABILITA_SCADENZE_READ', helpSlug: 'contabilita-scadenze-pagamenti', idPagina: 'SCADENZE_PAGAMENTI', title: 'Scadenza pagamenti' },
  },
  {
    path: '/contabilita/fornitori',
    name: 'contabilita-fornitori',
    component: () => import('@/modules/contabilita/views/FornitoriListView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'CONTABILITA_FORNITORI_READ', helpSlug: 'contabilita-fornitori', idPagina: 'FORNITORI', title: 'Fornitori' },
  },
  {
    path: '/contabilita/fornitori/nuovo',
    name: 'contabilita-fornitore-nuovo',
    component: () => import('@/modules/contabilita/views/FornitoreEditView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'CONTABILITA_FORNITORI_WRITE', helpSlug: 'contabilita-fornitori', idPagina: 'FORNITORE_EDIT', title: 'Nuovo fornitore' },
  },
  {
    path: '/contabilita/fornitori/:id(\\d+)',
    name: 'contabilita-fornitore',
    component: () => import('@/modules/contabilita/views/FornitoreEditView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'CONTABILITA_FORNITORI_READ', helpSlug: 'contabilita-fornitori', idPagina: 'FORNITORE_EDIT', title: 'Fornitore' },
  },
  {
    path: '/contabilita/documenti-spesa',
    name: 'contabilita-documenti-spesa',
    component: () => import('@/modules/contabilita/views/DocumentiSpesaView.vue'),
    meta: { hasTour: true,
      requiresAuth: true,
      permission: 'CONTABILITA_DOCUMENTI_READ',
      helpSlug: 'contabilita-documenti-spesa',
      idPagina: 'CONTABILITA_DOCUMENTI',
    },
  },
  {
    path: '/contabilita/documenti-spesa/nuovo',
    name: 'contabilita-documento-spesa-nuovo',
    component: () => import('@/modules/contabilita/views/DocumentoSpesaEditView.vue'),
    meta: { hasTour: true, requiresAuth: true, permissionAll: ['CONTABILITA_DOCUMENTI_READ', 'CONTABILITA_DOCUMENTI_WRITE'], helpSlug: 'contabilita-documento-spesa', idPagina: 'CONTABILITA_DOCUMENTO_EDIT', title: 'Nuovo documento di spesa' },
  },
  {
    path: '/contabilita/documenti-spesa/:id(\\d+)',
    name: 'contabilita-documento-spesa',
    component: () => import('@/modules/contabilita/views/DocumentoSpesaEditView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'CONTABILITA_DOCUMENTI_READ', helpSlug: 'contabilita-documento-spesa', idPagina: 'CONTABILITA_DOCUMENTO_EDIT', title: 'Documento di spesa' },
  },

  // --- Mezzi --------------------------------------------------------------
  {
    path: '/mezzi',
    name: 'mezzi-list',
    component: () => import('@/modules/mezzi/views/MezziListView.vue'),
    meta: {
      requiresAuth: true,
      // come il classico: la lista si apre con la lettura 2001 o con la scrittura 801 (movimenti)
      permissionAny: ['MEZZI_READ', 'MEZZI_WRITE', 'MEZZI_SCHEDA_WRITE'],
      helpSlug: 'elenco-mezzi',
      idPagina: 'MEZZI',
      hasTour: true,
    },
  },
  {
    path: '/mezzi/nuovo',
    name: 'mezzo-new',
    component: () => import('@/modules/mezzi/views/MezzoEditView.vue'),
    meta: {
      hasTour: true,
      requiresAuth: true,
      permission: 'MEZZI_SCHEDA_WRITE',
      helpSlug: 'scheda-mezzo',
    },
  },
  {
    path: '/mezzi/:id/edit',
    name: 'mezzo-edit',
    component: () => import('@/modules/mezzi/views/MezzoEditView.vue'),
    meta: {
      hasTour: true,
      requiresAuth: true,
      // scheda: modificabile con MEZZI_SCHEDA_WRITE; con il solo MEZZI_WRITE (801) resta in sola lettura
      // e si lavora su Movimenti/Rifornimenti (nel classico le tab si aprono con la 801 o con la scheda)
      permissionAny: ['MEZZI_SCHEDA_WRITE', 'MEZZI_WRITE'],
      helpSlug: 'scheda-mezzo',
      idPagina: 'MEZZO_EDIT',
    },
  },

  {
    path: '/strumenti/pdf',
    name: 'strumenti-pdf',
    component: () => import('@/modules/strumenti/views/StrumentiPdfView.vue'),
    meta: {
      // Nessun `permission`: la pagina non legge né scrive dati del gestionale — lavora sui file
      // che l'utente sceglie, dentro il browser.
      requiresAuth: true,
      helpSlug: 'strumenti-pdf',
      idPagina: 'STRUMENTI_PDF',
      hasTour: true,
    },
  },

  {
    path: '/strumenti/analisi-documenti',
    name: 'strumenti-analisi-documenti',
    component: () => import('@/modules/analisidocumenti/views/AnalisiDocumentiView.vue'),
    meta: {
      requiresAuth: true,
      // Archiviare usa i permessi legacy dei documenti; l'analisi con AI ANALISI_DOCUMENTI_USE: la pagina serve a entrambi
      permissionAny: ['BENEFICIARI_DOCUMENTI_OSPITE_WRITE', 'BENEFICIARI_DOCUMENTI_MEDICA_WRITE',
        'BENEFICIARI_DOCUMENTI_SOCIALE_WRITE', 'DOCUMENTALE_DOCUMENTI_CENTRO_WRITE', 'ANALISI_DOCUMENTI_USE'],
      helpSlug: 'analisi-documenti',
      idPagina: 'ANALISI_DOCUMENTI',
      hasTour: false,
      title: 'Carica e archivia documenti',
    },
  },
  {
    path: '/strumenti/analisi-documenti/casi',
    name: 'strumenti-analisi-documenti-casi',
    component: () => import('@/modules/analisidocumenti/views/AnalisiCasiView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'ANALISI_DOCUMENTI_ADMIN',
      helpSlug: 'analisi-documenti-casi',
      idPagina: 'ANALISI_DOCUMENTI_CASI',
      hasTour: false,
      title: 'Casi dell\'analisi documenti',
    },
  },

  {
    path: '/firma-digitale/mia',
    name: 'firma-digitale-mia',
    component: () => import('@/modules/firmaremota/views/MiaFirmaDigitaleView.vue'),
    // Sola lettura: le firme che l'utente può usare (le configura l'amministratore).
    meta: {
      hasTour: true,
      requiresAuth: true,
      permission: 'FIRMA_REMOTA_USE',
      helpSlug: 'firma-digitale-mia',
      idPagina: 'FIRMA_DIGITALE_MIA',
    },
  },

  {
    path: '/strumenti/firma-digitale',
    name: 'strumenti-firma-digitale',
    component: () => import('@/modules/firmaremota/views/StrumentiFirmaDigitaleView.vue'),
    // Il menu (voce 9128) lo filtra il backend; la pagina spiega cosa fare se la firma non è attiva.
    meta: {
      hasTour: true,
      requiresAuth: true,
      permission: 'FIRMA_REMOTA_USE',
      helpSlug: 'strumenti-firma-digitale',
      idPagina: 'STRUMENTI_FIRMA_DIGITALE',
    },
  },

  {
    path: '/strumenti/servizi-openapi',
    name: 'strumenti-servizi-openapi',
    component: () => import('@/modules/openapi/views/ServiziOpenApiView.vue'),
    // Aperta a chi ha OPENAPI_SERVIZI_USE e all'amministratore dell'azienda; il backend è autoritativo.
    // Voce di menu 9127 (Assistenza, dopo «Firma digitale»).
    meta: {
      hasTour: true,
      requiresAuth: true,
      permission: 'OPENAPI_SERVIZI_USE',
      oppureAmministratoreAzienda: true,
      title: 'Servizi OpenAPI',
      helpSlug: 'strumenti-servizi-openapi',
      idPagina: 'STRUMENTI_SERVIZI_OPENAPI',
    },
  },

  {
    path: '/firma-remota/configura',
    name: 'firma-remota-configura',
    component: () => import('@/modules/firmaremota/views/ConfiguraFirmaRemotaView.vue'),
    meta: {
      hasTour: true,
      requiresAuth: true,
      permission: 'FIRMA_REMOTA_ADMIN',
      // amministratore dell'azienda o permesso FIRMA_REMOTA_ADMIN (backend: @firmaRemotaAccesso)
      oppureAmministratoreAzienda: true,
      helpSlug: 'firma-remota-configura',
      idPagina: 'FIRMA_REMOTA_CONFIGURA',
    },
  },

  {
    path: '/mezzi/prenotazioni',
    name: 'mezzi-prenotazioni',
    component: () => import('@/modules/mezzi/views/MezziPrenotazioniView.vue'),
    meta: {
      requiresAuth: true,
      // chi può creare prenotazioni (801/6005) senza leggerle da 6004 vede le proprie richieste
      permissionAny: ['PRENOTAZIONE_MEZZO_READ', 'PRENOTAZIONE_MEZZO_CREATE'],
      helpSlug: 'prenotazione-mezzi',
      idPagina: 'MEZZI_PRENOTAZIONI',
      hasTour: true,
    },
  },

  {
    path: '/mezzi/workflow-config',
    name: 'mezzi-workflow-config',
    component: () => import('@/modules/mezzi/views/PrenotazioniMezziWorkflowConfigView.vue'),
    meta: {
      hasTour: true,
      requiresAuth: true,
      permission: 'PRENOTAZIONE_MEZZO_WORKFLOW_ADMIN',
      helpSlug: 'mezzi-workflow-config',
      idPagina: 'MEZZI_WORKFLOW_CONFIG',
    },
  },

  {
    path: '/mezzi/calendario',
    name: 'mezzi-calendario',
    component: () => import('@/modules/mezzi/views/MezziCalendarioView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'MEZZI_CALENDARIO_READ',
      helpSlug: 'calendario-mezzi',
      idPagina: 'MEZZI_CALENDARIO',
      hasTour: true,
    },
  },

  // --- Guasti e Manutenzioni (funz. 409) ------------------------------------
  {
    // Combacia con la spring_route della funzione legacy 409 (V475): '/guasti'
    path: '/guasti',
    name: 'guasti',
    component: () => import('@/modules/guasti/views/GuastiListView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'MANUTENZIONI_READ',
      helpSlug: 'guasti',
      hasTour: true,
      idPagina: 'GUASTI_LIST',
      title: 'Guasti e Manutenzioni',
      breadcrumb: [
        { label: 'Gestione Centri' },
        { label: 'Guasti e Manutenzioni', to: '/guasti' },
      ],
    },
  },
  {
    path: '/guasti/nuovo',
    name: 'guasto-nuovo',
    component: () => import('@/modules/guasti/views/GuastoEditView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'MANUTENZIONI_WRITE',
      helpSlug: 'guasto-edit',
      hasTour: true,
      idPagina: 'GUASTI_EDIT',
      title: 'Nuovo guasto',
      breadcrumb: [
        { label: 'Gestione Centri' },
        { label: 'Guasti e Manutenzioni', to: '/guasti' },
        { label: 'Nuovo guasto' },
      ],
    },
  },
  {
    path: '/guasti/:id(\\d+)',
    name: 'guasto-edit',
    component: () => import('@/modules/guasti/views/GuastoEditView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'MANUTENZIONI_READ',
      helpSlug: 'guasto-edit',
      hasTour: true,
      idPagina: 'GUASTI_EDIT',
      title: 'Scheda guasto',
      breadcrumb: [
        { label: 'Gestione Centri' },
        { label: 'Guasti e Manutenzioni', to: '/guasti' },
        { label: 'Scheda guasto' },
      ],
    },
  },

  // --- Atti vandalici / Eventi critici (funz. 407, Gestione Ospiti; spring_route V477) ---
  {
    path: '/atti-vandalici',
    name: 'atti-vandalici',
    component: () => import('@/modules/attivandalici/views/AttiVandaliciListView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'ATTI_VANDALICI_READ',
      helpSlug: 'atti-vandalici',
      hasTour: true,
      idPagina: 'ATTI_VANDALICI_LIST',
      title: 'Atti vandalici / Eventi critici',
      breadcrumb: [
        { label: 'Gestione Ospiti' },
        { label: 'Atti vandalici / Eventi critici', to: '/atti-vandalici' },
      ],
    },
  },
  {
    path: '/atti-vandalici/nuovo',
    name: 'atto-vandalico-nuovo',
    component: () => import('@/modules/attivandalici/views/AttoVandalicoEditView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'ATTI_VANDALICI_WRITE',
      helpSlug: 'atto-vandalico-edit',
      hasTour: true,
      idPagina: 'ATTI_VANDALICI_EDIT',
      title: 'Nuovo evento',
      breadcrumb: [
        { label: 'Gestione Ospiti' },
        { label: 'Atti vandalici / Eventi critici', to: '/atti-vandalici' },
        { label: 'Nuovo evento' },
      ],
    },
  },
  {
    path: '/atti-vandalici/:id(\\d+)',
    name: 'atto-vandalico-edit',
    component: () => import('@/modules/attivandalici/views/AttoVandalicoEditView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'ATTI_VANDALICI_READ',
      helpSlug: 'atto-vandalico-edit',
      hasTour: true,
      idPagina: 'ATTI_VANDALICI_EDIT',
      title: 'Scheda evento',
      breadcrumb: [
        { label: 'Gestione Ospiti' },
        { label: 'Atti vandalici / Eventi critici', to: '/atti-vandalici' },
        { label: 'Scheda evento' },
      ],
    },
  },

  // --- Rubrica (funzione legacy 221, Gestione Centri; spring_route V473) ---
  {
    path: '/rubrica',
    name: 'rubrica',
    component: () => import('@/modules/rubrica/views/RubricaListView.vue'),
    meta: {
      hasTour: true,
      requiresAuth: true, permission: 'RUBRICA_READ', helpSlug: 'rubrica', idPagina: 'RUBRICA', title: 'Rubrica',
      breadcrumb: [{ label: 'Gestione Centri' }, { label: 'Rubrica', to: '/rubrica' }],
    },
  },
  {
    path: '/rubrica/nuovo',
    name: 'rubrica-nuovo',
    component: () => import('@/modules/rubrica/views/RubricaEditView.vue'),
    meta: {
      hasTour: true,
      requiresAuth: true, permission: 'RUBRICA_WRITE', helpSlug: 'rubrica', idPagina: 'RUBRICA_EDIT', title: 'Nuovo contatto',
      breadcrumb: [{ label: 'Gestione Centri' }, { label: 'Rubrica', to: '/rubrica' }, { label: 'Nuovo contatto' }],
    },
  },
  {
    path: '/rubrica/:id(\\d+)',
    name: 'rubrica-edit',
    component: () => import('@/modules/rubrica/views/RubricaEditView.vue'),
    meta: {
      hasTour: true,
      requiresAuth: true, permission: 'RUBRICA_READ', helpSlug: 'rubrica', idPagina: 'RUBRICA_EDIT', title: 'Contatto',
      breadcrumb: [{ label: 'Gestione Centri' }, { label: 'Rubrica', to: '/rubrica' }, { label: 'Contatto' }],
    },
  },
  // --- Strutture ----------------------------------------------------------
  {
    path: '/strutture',
    name: 'strutture',
    component: () => import('@/modules/strutture/views/StruttureListView.vue'),
    meta: { requiresAuth: true, permission: 'STRUTTURE_READ', helpSlug: 'strutture', hasTour: true, idPagina: 'STRUTTURE_LIST', title: 'Strutture' },
  },
  {
    path: '/strutture/email-comuni',
    name: 'strutture-email-comuni',
    component: () => import('@/modules/strutture/views/EmailComuniView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'STRUTTURE_CREATE', helpSlug: 'strutture-email-comuni', idPagina: 'STRUTTURE_EMAIL_COMUNI', title: 'Elenco comuni' },
  },
  // Banche dell'azienda (funzione 6026 «Elenco Banche»). `nuova` PRIMA di `:id`.
  {
    path: '/strutture/banche',
    name: 'banche',
    component: () => import('@/modules/strutture/views/BancheListView.vue'),
    meta: { requiresAuth: true, permission: 'BANCHE_READ', helpSlug: 'banche', hasTour: true, idPagina: 'BANCHE_LIST', title: 'Banche' },
  },
  {
    path: '/strutture/banche/nuova',
    name: 'banca-nuova',
    component: () => import('@/modules/strutture/views/BancaEditView.vue'),
    meta: {
      requiresAuth: true, permission: 'BANCHE_WRITE', helpSlug: 'banca-edit', hasTour: true, idPagina: 'BANCHE_EDIT', title: 'Nuova banca',
      breadcrumb: [{ label: 'Configurazione', to: '/configurazione' }, { label: 'Banche', to: '/strutture/banche' }, { label: 'Nuova banca' }],
    },
  },
  {
    path: '/strutture/banche/:id(\\d+)',
    name: 'banca-edit',
    component: () => import('@/modules/strutture/views/BancaEditView.vue'),
    meta: {
      requiresAuth: true, permission: 'BANCHE_READ', helpSlug: 'banca-edit', hasTour: true, idPagina: 'BANCHE_EDIT', title: 'Banca',
      breadcrumb: [{ label: 'Configurazione', to: '/configurazione' }, { label: 'Banche', to: '/strutture/banche' }, { label: 'Banca' }],
    },
  },
  {
    path: '/strutture/report-spesa',
    name: 'strutture-report-spesa',
    component: () => import('@/modules/strutture/views/ReportSpesaView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'STRUTTURE_READ', helpSlug: 'strutture-report-spesa', idPagina: 'STRUTTURE_REPORT_SPESA', title: 'Report spesa' },
  },
  {
    path: '/strutture/liste-spesa',
    name: 'strutture-liste-spesa',
    component: () => import('@/modules/strutture/views/ElencoSpesaView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'STRUTTURE_READ', helpSlug: 'strutture-liste-spesa', idPagina: 'STRUTTURE_LISTE_SPESA', title: 'Elenco liste spese' },
  },
  {
    path: '/strutture/nuova',
    name: 'struttura-nuova',
    component: () => import('@/modules/strutture/views/StrutturaEditView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'STRUTTURE_CREATE', helpSlug: 'struttura-edit', idPagina: 'STRUTTURA_EDIT', title: 'Nuova struttura' },
  },
  {
    path: '/strutture/:id(\\d+)/spesa',
    name: 'struttura-lista-spesa',
    component: () => import('@/modules/strutture/views/StrutturaListaSpesaView.vue'),
    meta: { requiresAuth: true, permission: 'STRUTTURE_LISTASPESA', helpSlug: 'struttura-lista-spesa', hasTour: true, idPagina: 'STRUTTURA_LISTA_SPESA', title: 'Lista spesa' },
  },
  {
    path: '/strutture/:id(\\d+)/spesa/nuova',
    name: 'struttura-lista-spesa-nuova',
    component: () => import('@/modules/strutture/views/StrutturaListaSpesaEditView.vue'),
    meta: { requiresAuth: true, permission: 'STRUTTURE_LISTASPESA', helpSlug: 'struttura-lista-spesa', hasTour: true, idPagina: 'STRUTTURA_LISTA_SPESA_EDIT', title: 'Nuova lista spesa' },
  },
  {
    path: '/strutture/:id(\\d+)/spesa/:spesaId(\\d+)',
    name: 'struttura-lista-spesa-edit',
    component: () => import('@/modules/strutture/views/StrutturaListaSpesaEditView.vue'),
    meta: { requiresAuth: true, permission: 'STRUTTURE_LISTASPESA', helpSlug: 'struttura-lista-spesa', hasTour: true, idPagina: 'STRUTTURA_LISTA_SPESA_EDIT', title: 'Lista spesa' },
  },
  {
    path: '/strutture/:id(\\d+)/kit',
    name: 'struttura-kit',
    component: () => import('@/modules/strutture/views/StrutturaKitView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'STRUTTURE_LISTASPESA', helpSlug: 'struttura-kit', idPagina: 'STRUTTURA_KIT', title: 'Kit della struttura' },
  },
  {
    path: '/strutture/:id(\\d+)',
    name: 'struttura-edit',
    component: () => import('@/modules/strutture/views/StrutturaEditView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'STRUTTURE_READ', helpSlug: 'struttura-edit', idPagina: 'STRUTTURA_EDIT', title: 'Struttura' },
  },

  // --- Centri (gestione) --------------------------------------------------
  {
    path: '/centri',
    name: 'centri',
    component: () => import('@/modules/strutture/views/CentriListView.vue'),
    meta: { requiresAuth: true, permission: 'CENTRI_READ', helpSlug: 'centri', hasTour: true, idPagina: 'CENTRI_LIST' },
  },
  {
    path: '/centri/new',
    name: 'centro-new',
    component: () => import('@/modules/strutture/views/CentroEditView.vue'),
    meta: { requiresAuth: true, permission: 'CENTRI_WRITE', helpSlug: 'centro-edit', hasTour: true, idPagina: 'CENTRI_EDIT' },
  },
  // NOTA: /centri/:id/notifiche deve stare PRIMA di /centri/:id per evitare che "notifiche"
  // venga interpretato come id numerico.
  {
    path: '/centri/:id/notifiche',
    name: 'centro-notifiche',
    component: () => import('@/modules/strutture/views/CentroNotificheView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'CENTRI_READ',
      helpSlug: 'centro-notifiche-regole',
      hasTour: true,
      idPagina: 'CENTRO_NOTIFICHE_REGOLE',
      title: 'Regole di Notifica Centro',
    },
  },
  // Convenzioni, Cassa e Articoli del centro: pagine proprie (prima erano tab della scheda). Prima di /centri/:id.
  {
    path: '/centri/:id(\\d+)/convenzioni',
    name: 'centro-convenzioni',
    component: () => import('@/modules/strutture/views/CentroSezioneView.vue'),
    meta: { requiresAuth: true, permission: 'CENTRI_WRITE', helpSlug: 'centro-convenzioni', hasTour: true, idPagina: 'CENTRO_CONVENZIONI', sezione: 'convenzioni', title: 'Convenzioni del centro' },
  },
  {
    path: '/centri/:id(\\d+)/cassa',
    name: 'centro-cassa',
    component: () => import('@/modules/strutture/views/CentroSezioneView.vue'),
    meta: { requiresAuth: true, permission: 'CENTRI_CASSA_WRITE', helpSlug: 'centro-cassa', hasTour: true, idPagina: 'CENTRO_CASSA', sezione: 'cassa', title: 'Cassa del centro' },
  },
  {
    path: '/centri/:id(\\d+)/articoli',
    name: 'centro-articoli',
    component: () => import('@/modules/strutture/views/CentroSezioneView.vue'),
    meta: { requiresAuth: true, permission: 'CENTRI_ARTICOLI_WRITE', helpSlug: 'centro-articoli', hasTour: true, idPagina: 'CENTRO_ARTICOLI', sezione: 'articoli', title: 'Articoli associati al centro' },
  },
  {
    path: '/centri/:id(\\d+)/dati-tecnici',
    name: 'centro-dati-tecnici',
    component: () => import('@/modules/strutture/views/CentroDatiTecniciView.vue'),
    meta: { requiresAuth: true, permission: 'CENTRI_DATI_TECNICI_WRITE', helpSlug: 'centro-dati-tecnici', hasTour: true, idPagina: 'CENTRO_DATI_TECNICI', title: 'Utenze e dati tecnici del centro' },
  },
  {
    path: '/centri/:id',
    name: 'centro-edit',
    component: () => import('@/modules/strutture/views/CentroEditView.vue'),
    // Compatibilità: i vecchi link `?tab=convenzioni|cassa|articoli` (anche con `&conv=`) aprono le pagine dedicate.
    beforeEnter: (to) => {
      const tab = to.query.tab
      if ((tab === 'convenzioni' || tab === 'cassa' || tab === 'articoli') && /^\d+$/.test(String(to.params.id))) {
        const resto = { ...to.query }
        delete resto.tab
        return { path: `/centri/${String(to.params.id)}/${tab}`, query: resto, replace: true }
      }
      return true
    },
    meta: { requiresAuth: true, permission: 'CENTRI_READ', helpSlug: 'centro-edit', hasTour: true, idPagina: 'CENTRI_EDIT' },
  },

  // --- Azienda ------------------------------------------------------------
  {
    path: '/azienda',
    name: 'azienda',
    component: () => import('@/modules/azienda/views/AziendaView.vue'),
    meta: { requiresAuth: true, permission: 'AZIENDA_READ', helpSlug: 'azienda', hasTour: true },
  },
  // La vecchia pagina (saldo, catalogo e consumi) è assorbita dal pannello operativo in Assistenza.
  // Gruppi operatore (Equipe): abilitazione per azienda e permesso di modifica sono decisi dal backend (`/stato`)
  {
    path: '/gruppi-operatore',
    name: 'gruppi-operatore',
    component: () => import('@/modules/gruppi/views/GruppiOperatoreView.vue'),
    meta: {
      requiresAuth: true,
      helpSlug: 'gruppi-operatore',
      hasTour: true,
      idPagina: 'GRUPPI_OPERATORE',
      title: 'Equipe',
      breadcrumb: [
        { label: 'Configurazione', to: '/configurazione' },
        { label: 'Equipe' },
      ],
    },
  },
  // Motivi di uscita dell'azienda (funzione 2202 «Elenco Motivo Uscita»)
  {
    path: '/azienda/motivi-uscita',
    name: 'motivi-uscita',
    component: () => import('@/modules/azienda/views/MotiviUscitaView.vue'),
    meta: { requiresAuth: true, permission: 'MOTIVI_USCITA_READ', helpSlug: 'motivi-uscita', hasTour: true, idPagina: 'MOTIVI_USCITA', title: 'Motivi di uscita' },
  },
  // Modelli badge: scelta del modello per azienda e centri (voce «Configura modelli badge» in Configurazione)
  {
    path: '/azienda/modelli-badge',
    name: 'modelli-badge',
    component: () => import('@/modules/modellibadge/views/ModelliBadgeView.vue'),
    meta: { requiresAuth: true, permission: 'AZIENDA_WRITE', helpSlug: 'modelli-badge', hasTour: true, idPagina: 'MODELLI_BADGE', title: 'Modelli badge' },
  },
  // Catalogo dei modelli badge (voce in «Configurazione Admin»): solo amministratore di piattaforma
  {
    path: '/admin/badge-catalogo',
    name: 'admin-badge-catalogo',
    component: () => import('@/modules/modellibadge/views/CatalogoBadgeAdminView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'PLATFORM_SUPERADMIN', helpSlug: 'admin-badge-catalogo', idPagina: 'ADMIN_BADGE_CATALOGO', title: 'Catalogo modelli badge' },
  },
  { path: '/azienda/servizi-openapi', redirect: '/strumenti/servizi-openapi' },
  {
    path: '/azienda/edit',
    name: 'azienda-edit',
    component: () => import('@/modules/azienda/views/AziendaEditView.vue'),
    meta: { requiresAuth: true, permission: 'AZIENDA_WRITE', helpSlug: 'azienda-edit', idPagina: 'AZIENDA_EDIT' },
  },
  {
    path: '/azienda/campi-custom',
    name: 'azienda-campi-custom',
    component: () => import('@/modules/azienda/views/CampiCustomAziendaView.vue'),
    meta: {
      hasTour: true,
      requiresAuth: true,
      permission: 'PLATFORM_SUPERADMIN',
      helpSlug: 'azienda-campi-custom',
    },
  },

  // --- Mail / PEC ---------------------------------------------------------
  {
    path: '/mail/inbox',
    name: 'webmail',
    component: () => import('@/modules/mail/views/WebmailView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'MAIL_READ',
      idPagina: 'WEBMAIL',
      helpSlug: 'webmail',
      hasTour: true,
      title: 'Webmail',
    },
  },
  {
    path: '/mail/pec/notifiche',
    name: 'pec-notifiche',
    component: () => import('@/modules/mail/views/PecNotificheView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'MAIL_READ',
      helpSlug: 'webmail',
      title: 'Notifiche PEC',
    },
  },
  {
    // Rimbalzo dopo l'autorizzazione OAuth2: il backend non sa chi torna dal provider, qui sì.
    path: '/mail/oauth-esito',
    name: 'mail-oauth-esito',
    component: () => import('@/modules/mail/views/MailOAuthEsitoView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/mail/accounts',
    name: 'mail-accounts',
    component: () => import('@/modules/mail/views/MailAccountListView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'MAIL_CONFIG_READ', helpSlug: 'mail-accounts' },
  },
  {
    path: '/mail/accounts/new',
    name: 'mail-account-new',
    component: () => import('@/modules/mail/views/MailAccountFormView.vue'),
    meta: { requiresAuth: true, permission: 'MAIL_CONFIG_WRITE', helpSlug: 'mail-accounts' },
  },
  {
    path: '/mail/accounts/:id/edit',
    name: 'mail-account-edit',
    component: () => import('@/modules/mail/views/MailAccountFormView.vue'),
    meta: { requiresAuth: true, permission: 'MAIL_CONFIG_WRITE', helpSlug: 'mail-accounts' },
  },

  // --- Documentale: Tipologie ---------------------------------------------
  {
    path: '/tipologie-documentali',
    name: 'tipologie-documentali',
    component: () => import('@/modules/documentale/views/TipologieDocumentaliView.vue'),
    meta: {
      hasTour: true,
      requiresAuth: true,
      permission: 'DOCUMENTALE_TIPOLOGIE_READ',
      helpSlug: 'tipologie-documentali',
      idPagina: 'TIPOLOGIE_DOCUMENTALI',
    },
  },

  // --- Mattinali automatici a plugin (gestiti da Spring) ---------------------
  {
    path: '/mattinali/configurazione',
    name: 'mattinali-configurazione',
    component: () => import('@/modules/mattinali/views/MattinaliConfigView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'MATTINALE_JOB_ADMIN',
      helpSlug: 'mattinali-automatici',
      idPagina: 'MATTINALI_CONFIG',
    },
  },
  {
    path: '/mattinali/configurazione/nuovo',
    name: 'mattinali-configurazione-nuovo',
    component: () => import('@/modules/mattinali/views/MattinaleEditView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'MATTINALE_JOB_ADMIN',
      helpSlug: 'mattinali-automatici',
      idPagina: 'MATTINALI_CONFIG',
    },
  },
  {
    path: '/mattinali/configurazione/:id(\\d+)',
    name: 'mattinali-configurazione-modifica',
    component: () => import('@/modules/mattinali/views/MattinaleEditView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'MATTINALE_JOB_ADMIN',
      helpSlug: 'mattinali-automatici',
      idPagina: 'MATTINALI_CONFIG',
    },
  },

  // --- Vulnerabilità: Tipologie (funz. 718) -------------------------------
  {
    path: '/tipologie-vulnerabilita',
    name: 'tipologie-vulnerabilita',
    component: () => import('@/modules/vulnerabilita/views/TipologieVulnerabilitaView.vue'),
    meta: {
      hasTour: true,
      requiresAuth: true,
      permission: 'TIPOLOGIE_VULNERABILITA_READ',
      helpSlug: 'tipologie-vulnerabilita',
      idPagina: 'TIPOLOGIE_VULNERABILITA',
    },
  },

  // --- Documentale: Documenti Centro (funz. 902) --------------------------
  {
    path: '/documenti-centro',
    name: 'documenti-centro',
    component: () => import('@/modules/documentale/views/DocumentiCentroView.vue'),
    meta: {
      hasTour: true,
      requiresAuth: true,
      permission: 'DOCUMENTALE_DOCUMENTI_CENTRO_READ',
      helpSlug: 'documenti-centro',
      idPagina: 'documenti-centro',
      title: 'Documenti del Centro',
      breadcrumb: [
        { label: 'Documentale' },
        { label: 'Documenti del Centro', to: '/documenti-centro' },
      ],
    },
  },

  // --- Documentale: Modelli Documenti (funz. 908) -------------------------
  {
    path: '/modelli',
    name: 'modelli',
    component: () => import('@/modules/documentale/views/ModelliView.vue'),
    meta: {
      hasTour: true,
      requiresAuth: true,
      permission: 'DOCUMENTALE_MODELLI_READ',
      helpSlug: 'modelli',
      idPagina: 'modelli',
      title: 'Modelli Documenti',
      breadcrumb: [
        { label: 'Documentale' },
        { label: 'Modelli Documenti', to: '/modelli' },
      ],
    },
  },

  // --- Documentale: Elenco Scadenze (funz. 905) ---------------------------
  {
    path: '/elenco-scadenze',
    name: 'elenco-scadenze',
    component: () => import('@/modules/documentale/views/ElencoScadenzeView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'DOCUMENTALE_SCADENZE_READ',
      helpSlug: 'elenco-scadenze',
      idPagina: 'elenco-scadenze',
      hasTour: true,
      title: 'Elenco Scadenze',
      breadcrumb: [
        { label: 'Documentale' },
        { label: 'Elenco Scadenze', to: '/elenco-scadenze' },
      ],
    },
  },

  // --- Protocollo ---------------------------------------------------------
  {
    // Combacia con la spring_route della funzione legacy 116 (V417): '/buste-paga'
    path: '/buste-paga',
    component: () => import('@/modules/bustepaga/views/BustePagaView.vue'),
    meta: {
      requiresAuth: true,
      idPagina: 'BUSTE_PAGA',
      helpSlug: 'buste-paga',
      // Senza questo flag il tour parte solo al primo accesso e poi non c'è più modo di
      // rivederlo: il pulsante ▶ nella barra in alto compare solo se la route lo dichiara.
      hasTour: true,
    },
  },
  {
    path: '/protocollo/elenco',
    component: () => import('@/modules/protocollo/views/ProtocolloListView.vue'),
    meta: {
      requiresAuth: true,
      idPagina: 'PROTOCOLLO_ELENCO',
      helpSlug: 'protocollo-elenco',
      hasTour: true,
    },
  },
  {
    // Coda dei protocolli che l'utente deve firmare (approvare). Il path combacia con la spring_route della
    // voce «Firma Protocollo» (funzioni_2 id 2402). Il gate è PROTOCOLLO_READ: chi firma perché è indicato nel
    // workflow non ha necessariamente PROTOCOLLO_FIRM, e a decidere cosa vede è il server.
    path: '/protocollo/da-firmare',
    component: () => import('@/modules/protocollo/views/ProtocolliDaFirmareView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'PROTOCOLLO_READ',
      idPagina: 'PROTOCOLLO_DA_FIRMARE',
      helpSlug: 'protocollo-da-firmare',
    },
  },
  {
    // Il path combacia con la spring_route del menu (funzioni_2 id 9110, V255): '/protocolli/...'
    path: '/protocolli/workflow-config',
    component: () => import('@/modules/protocollo/views/ProtocolloWorkflowConfigView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'PROTOCOLLO_WORKFLOW_ADMIN',
      idPagina: 'PROTOCOLLO_WORKFLOW_CONFIG',
      helpSlug: 'protocollo-workflow-config',
    },
  },

  // --- Pagina di errore ------------------------------------------------
  {
    path: '/error',
    name: 'error',
    component: () => import('@/views/ErrorView.vue'),
    meta: { requiresAuth: false, publicLayout: true },
  },

  // --- Ordini Magazzino ------------------------------------------------
  // Le route statiche precedono /articoli/:id per non farsi "catturare" dal parametro.
  {
    path: '/articoli/nuovo',
    name: 'articolo-nuovo',
    component: () => import('@/modules/magazzino/views/ArticoloEditView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'ARTICOLI_WRITE', helpSlug: 'articoli' },
  },
  {
    path: '/articoli/:id',
    name: 'articolo-scheda',
    component: () => import('@/modules/magazzino/views/ArticoloEditView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'ARTICOLI_READ', helpSlug: 'articoli' },
  },
  {
    path: '/articoli',
    name: 'articoli',
    component: () => import('@/modules/magazzino/views/ArticoliListView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'ARTICOLI_READ', helpSlug: 'articoli', idPagina: 'ARTICOLI' },
  },
  {
    path: '/magazzino/report',
    name: 'magazzino-report',
    component: () => import('@/modules/magazzino/views/ReportMagazzinoView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'MAGAZZINO_REPORT_READ', helpSlug: 'magazzino-report', idPagina: 'MAGAZZINO_REPORT', title: 'Report Magazzino' },
  },
  {
    path: '/ospiti/report',
    name: 'ospiti-report',
    component: () => import('@/modules/reportospiti/views/ReportOspitiView.vue'),
    meta: { hasTour: false, requiresAuth: true, permission: 'OSPITI_REPORT_READ', helpSlug: 'ospiti-report', idPagina: 'OSPITI_REPORT', title: 'Report Ospiti' },
  },
  {
    path: '/personale/report',
    name: 'personale-report',
    component: () => import('@/modules/personale/views/ReportPersonaleView.vue'),
    meta: { hasTour: false, requiresAuth: true, permission: 'PERSONALE_REPORT_READ', helpSlug: 'personale-report', idPagina: 'PERSONALE_REPORT', title: 'Report Personale' },
  },
  {
    path: '/ospiti/report/relazioni-periodiche',
    name: 'ospiti-report-relazioni-periodiche',
    component: () => import('@/modules/relazioni/views/RelazioniPeriodicheView.vue'),
    meta: { hasTour: false, requiresAuth: true, permission: 'RELAZIONI_PERIODICHE_USE', helpSlug: 'relazioni-periodiche', idPagina: 'RELAZIONI_PERIODICHE', title: 'Relazioni periodiche' },
  },
  {
    path: '/ospiti/report/analisi-personalizzate',
    name: 'ospiti-report-analisi-personalizzate',
    component: () => import('@/modules/relazioni/views/AnalisiPersonalizzateView.vue'),
    meta: { hasTour: false, requiresAuth: true, permission: 'RELAZIONI_PERIODICHE_USE', helpSlug: 'analisi-personalizzate', idPagina: 'ANALISI_PERSONALIZZATE', title: 'Analisi personalizzate' },
  },
  {
    path: '/configurazione/relazioni-modelli',
    name: 'configurazione-relazioni-modelli',
    component: () => import('@/modules/relazioni/views/RelazioniModelliView.vue'),
    meta: { hasTour: false, requiresAuth: true, permission: 'RELAZIONI_PERIODICHE_ADMIN', helpSlug: 'relazioni-modelli', idPagina: 'RELAZIONI_MODELLI', title: 'Modelli di relazione' },
  },
  {
    path: '/magazzino/diario',
    name: 'magazzino-diario',
    component: () => import('@/modules/magazzino/views/MagazzinoDiarioView.vue'),
    meta: { hasTour: true, requiresAuth: true, permissionAny: ['GIACENZE_READ', 'MAGAZZINO_VERIFICA_DDT'], helpSlug: 'magazzino-diario', idPagina: 'MAGAZZINO_DIARIO', title: 'Diario del magazzino' },
  },
  {
    path: '/magazzino/report/consegne-periodo',
    name: 'magazzino-report-consegne-periodo',
    component: () => import('@/modules/magazzino/views/ReportConsegneView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'MAGAZZINO_STAMPA_CONSEGNE', helpSlug: 'magazzino-report-consegne', idPagina: 'MAGAZZINO_REPORT_CONSEGNE', title: 'Report consegne per periodo', tipoReport: 'periodo' },
  },
  {
    path: '/magazzino/report/consegne-centro',
    name: 'magazzino-report-consegne-centro',
    component: () => import('@/modules/magazzino/views/ReportConsegneView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'MAGAZZINO_STAMPA_CONSEGNE', helpSlug: 'magazzino-report-consegne', idPagina: 'MAGAZZINO_REPORT_CONSEGNE', title: 'Report consegne materiale centro', tipoReport: 'materiale-centro' },
  },
  {
    path: '/magazzino/report/consegne-ospite',
    name: 'magazzino-report-consegne-ospite',
    component: () => import('@/modules/magazzino/views/ReportConsegneView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'MAGAZZINO_STAMPA_CONSEGNE', helpSlug: 'magazzino-report-consegne', idPagina: 'MAGAZZINO_REPORT_CONSEGNE', title: 'Report consegne per ospite', tipoReport: 'ospite' },
  },
  {
    path: '/magazzino/report/effetti-da-consegnare',
    name: 'magazzino-report-effetti-da-consegnare',
    component: () => import('@/modules/magazzino/views/EffettiDaConsegnareView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'MAGAZZINO_STAMPA_COMPUTO_EFFETTI', helpSlug: 'magazzino-report-effetti-da-consegnare', idPagina: 'MAGAZZINO_REPORT_EFFETTI_DA_CONSEGNARE', title: 'Report effetti da consegnare' },
  },
  {
    path: '/magazzino/report/articoli-consegnati',
    name: 'magazzino-report-articoli-consegnati',
    component: () => import('@/modules/magazzino/views/ArticoliConsegnatiView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'MAGAZZINO_STAMPA_CONSEGNE', helpSlug: 'magazzino-report-articoli-consegnati', idPagina: 'MAGAZZINO_REPORT_ARTICOLI_CONSEGNATI', title: 'Report articoli consegnati' },
  },
  {
    path: '/magazzino/report/movimenti-articolo',
    name: 'magazzino-report-movimenti-articolo',
    component: () => import('@/modules/magazzino/views/MovimentiArticoloView.vue'),
    meta: { hasTour: true, requiresAuth: true, permissionAny: ['MAGAZZINO_VERIFICA_DDT', 'GIACENZE_READ'], helpSlug: 'magazzino-report-articoli', idPagina: 'MAGAZZINO_REPORT_MOVIMENTI', title: 'Movimenti articolo' },
  },
  {
    path: '/magazzino/report/articoli-magazzino',
    name: 'magazzino-report-articoli-magazzino',
    component: () => import('@/modules/magazzino/views/ArticoliMagazzinoReportView.vue'),
    meta: { hasTour: true, requiresAuth: true, permissionAny: ['MAGAZZINO_VERIFICA_DDT', 'GIACENZE_READ'], helpSlug: 'magazzino-report-articoli', idPagina: 'MAGAZZINO_REPORT_ARTICOLI', title: 'Articoli di magazzino' },
  },
  {
    path: '/giacenze',
    name: 'giacenze',
    component: () => import('@/modules/magazzino/views/GiacenzeListView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'GIACENZE_READ', helpSlug: 'giacenze', idPagina: 'GIACENZE' },
  },
  // Le route statiche precedono /ordini/:id per non farsi "catturare" dal parametro.
  {
    path: '/ordini',
    name: 'ordini-list',
    component: () => import('@/modules/ordini/views/OrdiniListView.vue'),
    meta: { requiresAuth: true, permission: 'ORDINI_READ', helpSlug: 'ordini-lista', hasTour: true, idPagina: 'ORDINI' },
  },
  {
    path: '/ordini/nuovo',
    name: 'ordini-nuovo',
    component: () => import('@/modules/ordini/views/NuovoOrdineView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'ORDINI_CREATE', helpSlug: 'ordini-lista' },
  },
  {
    // Stesso componente della creazione: il form è identico, cambia solo da dove partono i dati
    // e dove finiscono al salvataggio.
    path: '/ordini/:id/modifica',
    name: 'ordini-modifica',
    component: () => import('@/modules/ordini/views/NuovoOrdineView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'ORDINI_CREATE', helpSlug: 'ordini-lista' },
  },
  {
    // Correzione delle righe di un ordine già inviato: chi può lo decide il server (azione
    // CORREGGI del dettaglio), per questo la route chiede solo la lettura.
    path: '/ordini/:id/correzione',
    name: 'ordini-correzione',
    component: () => import('@/modules/ordini/views/OrdineCorrezioneView.vue'),
    meta: { requiresAuth: true, permission: 'ORDINI_READ', helpSlug: 'ordini-lista' },
  },
  {
    path: '/ordini/da-approvare',
    name: 'ordini-da-approvare',
    component: () => import('@/modules/ordini/views/DaApprovareView.vue'),
    meta: { requiresAuth: true, permission: 'ORDINI_READ', helpSlug: 'ordini-lista', hasTour: true, idPagina: 'ORDINI' },
  },
  {
    path: '/ordini/workflow-config',
    name: 'ordini-workflow-config',
    component: () => import('@/modules/ordini/views/OrdiniWorkflowConfigView.vue'),
    meta: { requiresAuth: true, permission: 'ORDINI_WORKFLOW_ADMIN', helpSlug: 'ordini-workflow-config', hasTour: true },
  },
  {
    path: '/ordini/:id',
    name: 'ordini-detail',
    component: () => import('@/modules/ordini/views/OrdineDetailView.vue'),
    meta: { requiresAuth: true, permission: 'ORDINI_READ', helpSlug: 'ordini-lista' },
  },

  // --- Catch-all: 404 --------------------------------------------------
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/ErrorView.vue'),
    meta: { requiresAuth: false, publicLayout: true },
  },
  {
    path: '/admin/plugins',
    name: 'admin-plugins',
    component: () => import('@/modules/admin/views/PluginAdminView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'PLATFORM_SUPERADMIN', title: 'Gestione Plugin', helpSlug: 'admin-plugins' },
  },
  {
    // Funzioni passpartout sbloccate: solo in sessione passpartout (non serve essere super-admin né una voce di menu:
    // ci si arriva dal badge «Passpartout» e dalla pagina Configurazione Pagine).
    path: '/admin/funzioni-passpartout',
    name: 'admin-funzioni-passpartout',
    component: () => import('@/modules/admin/views/FunzioniPasspartoutAdminView.vue'),
    meta: { requiresAuth: true, soloPasspartout: true, title: 'Funzioni passpartout sbloccate', helpSlug: 'admin-funzioni-passpartout' },
  },
  {
    path: '/admin/pagine-config',
    name: 'admin-pagine-config',
    component: () => import('@/modules/admin/views/PaginaConfigListView.vue'),
    meta: { hasTour: true, requiresAuth: true, permission: 'PLATFORM_SUPERADMIN', helpSlug: 'admin-pagine-config' },
  },
  {
    path: '/admin/pagine-config/new',
    name: 'admin-pagine-config-new',
    component: () => import('@/modules/admin/views/PaginaConfigFormView.vue'),
    meta: { requiresAuth: true, permission: 'PLATFORM_SUPERADMIN', helpSlug: 'admin-pagine-config' },
  },
  {
    path: '/admin/pagine-config/:id/edit',
    name: 'admin-pagine-config-edit',
    component: () => import('@/modules/admin/views/PaginaConfigFormView.vue'),
    meta: { requiresAuth: true, permission: 'PLATFORM_SUPERADMIN', helpSlug: 'admin-pagine-config' },
  },

  // --- Admin: regole di notifica (alert) ------------------------------------
  // RIMOSSA: la pagina admin globale /admin/notifiche-regole è stata sostituita
  // dall'editor per-centro /centri/:id/notifiche (CentroNotificheView).
  // La view NotificheRegoleView.vue è stata rimossa.
  // La guida admin-notifiche-regole (V173) è orfana — da gestire lato backend.
  //
  // Redirect di compatibilità: chi arriva al vecchio path viene rimandato ai centri.
  {
    path: '/admin/notifiche-regole',
    redirect: '/centri',
  },

  // --- Admin: servizio unificato notifiche (STEP 1) ------------------------
  {
    path: '/admin/notifiche/template',
    name: 'admin-notifiche-template',
    component: () => import('@/modules/admin/views/NotificaTemplateAdminView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'I18N_ADMIN',
      helpSlug: 'notifiche-template-admin',
      title: 'Template Notifiche',
    },
  },
  {
    path: '/admin/notifiche/test',
    name: 'admin-notifiche-test',
    component: () => import('@/modules/admin/views/NotificheTestView.vue'),
    meta: {
      hasTour: true,
      requiresAuth: true,
      permission: 'I18N_ADMIN',
      helpSlug: 'notifiche-test-admin',
      title: 'Test Invio Notifiche',
    },
  },
  // --- Admin: WhatsApp azienda (STEP 2) ------------------------------------
  {
    path: '/admin/whatsapp',
    name: 'admin-whatsapp',
    component: () => import('@/modules/admin/views/WhatsAppAziendaView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'WHATSAPP_ADMIN',
      helpSlug: 'admin-whatsapp',
      idPagina: 'ADMIN_WHATSAPP',
      hasTour: true,
      title: 'Configura WhatsApp',
    },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()
  await auth.ensureLoaded()

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  // Se già autenticato e tenta di andare al login, redirect alla dashboard
  if (to.name === 'login' && auth.isAuthenticated) {
    return { name: 'dashboard' }
  }

  // Guard permesso route: se specificato, l'utente deve averlo.
  // `oppureAmministratoreAzienda` apre la route anche all'amministratore dell'azienda
  // (flag utenti.amministratore) quando la pagina mostra dati confinati alla sua azienda.
  if (to.meta.soloPasspartout && auth.isAuthenticated && auth.me?.passpartout !== true) {
    return { name: 'dashboard' }
  }
  if (to.meta.passpartoutKey && auth.isAuthenticated) {
    const sbloccate = useFunzioniSbloccateStore()
    // le sbloccate arrivano dal server: attendo il primo caricamento (in errore resta la sola regola passpartout)
    await sbloccate.carica()
    if (!sbloccate.visibile(to.meta.passpartoutKey)) return { name: 'dashboard' }
  }
  if (to.meta.permissionAny?.length && auth.isAuthenticated && !to.meta.permissionAny.some((p) => auth.has(p))) {
    if (!(to.meta.oppureRuoloAmministratore && haRuoloAmministratore(auth.me?.roles))) {
      return { name: 'dashboard' }
    }
  }
  if (to.meta.permissionAll?.length && auth.isAuthenticated && !to.meta.permissionAll.every((p) => auth.has(p))) {
    return { name: 'dashboard' }
  }
  if (to.meta.permission && auth.isAuthenticated && !auth.has(to.meta.permission)) {
    const ammAzienda = auth.isPlatformSuperAdmin || !!auth.me?.amministratoreGenerale
    if (!(to.meta.oppureAmministratoreAzienda && ammAzienda)) {
      return { name: 'dashboard' }
    }
  }
})

export default router
